import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
    DocumentMigrationIssueDto,
    CoADocumentMigrationSource,
    documentMigrationUploadErrorSchema,
    GlBasis,
    QbdFileMigrationUploadSource,
} from "@common/schemas/accounting-integration.schema";
import { DocumentMigrationErrorCode } from "@common/constants/accounting-migration";
import { ImportRejection } from "@common/schemas/domains/accounting/import-rejection.schema";
import { isAxiosError } from "axios";
import { useScope } from "@state/scopeContext.lib";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { getEntitiesAccountsQueryKey } from "@queries/entitiesAccounts";
import {
    accountingIntegrationContractClient,
    accountingMigrationQueryKeys,
} from "./accountingIntegration.shared";
import { tasksQueryKeys } from "./task.api";
import { invalidateAllTaskQueries } from "./taskQueryKeys";

type DocumentMigrationUploadError = z.infer<
    typeof documentMigrationUploadErrorSchema
>;

export class DocumentMigrationError extends Error {
    constructor(public readonly detail: DocumentMigrationUploadError) {
        super(detail.message);
        this.name = "DocumentMigrationError";
    }
}

export const MIGRATION_IMPORT_FAILED_TOAST =
    "Failed to start the import. Please try again.";
export const MIGRATION_EXTRACTION_FAILED_TOAST =
    "Failed to extract the file. Please try again.";

export function documentMigrationErrorToastMessage(
    error: unknown,
    fallback: string,
): string {
    if (!(error instanceof DocumentMigrationError)) {
        return fallback;
    }
    const rejection = error.detail.rejection;
    if (rejection) {
        return rejection.guidance
            ? `${rejection.summary}. ${rejection.guidance}`
            : rejection.summary;
    }
    return error.detail.message;
}

function rethrowAsMigrationError(error: unknown): never {
    if (isAxiosError(error) && error.response?.status === 400) {
        const parsed = documentMigrationUploadErrorSchema.safeParse(
            error.response.data,
        );
        if (parsed.success) {
            throw new DocumentMigrationError(parsed.data);
        }
    }
    throw error;
}

export async function setGlExtractionBasis(params: {
    workspaceId: string;
    entityId: number;
    documentId: number;
    basis: GlBasis;
}) {
    const { workspaceId, entityId, documentId, basis } = params;

    await accountingIntegrationContractClient.setGlExtractionBasis({
        params: { workspaceId, entityId },
        body: { documentId, basis },
    });
}

export async function extractMigrationFileById(params: {
    workspaceId: string;
    entityId: number;
    source: CoADocumentMigrationSource;
    documentId: number;
}) {
    const { workspaceId, entityId, source, documentId } = params;

    try {
        const response =
            await accountingIntegrationContractClient.extractMigrationFile({
                params: { workspaceId, entityId, source },
                body: { documentId },
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });

        return { ...response.body, documentId };
    } catch (error) {
        rethrowAsMigrationError(error);
    }
}

export interface SubmitDocumentMigrationParams {
    coaDocumentId: number;
    glDocumentIds: number[];
}

function useInvalidateMigrationQueriesOnSuccess(entityId: number) {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();

    return () => {
        queryClient.invalidateQueries({
            queryKey: accountingMigrationQueryKeys.byEntity(entityId),
        });
        queryClient.invalidateQueries({
            queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
        });
    };
}

export function useSubmitDocumentMigrationMutation(params: {
    workspaceId: string;
    entityId: number;
    source: CoADocumentMigrationSource;
}) {
    const { workspaceId, entityId, source } = params;

    return useMutation({
        mutationFn: async (documentIds: SubmitDocumentMigrationParams) => {
            try {
                const response =
                    await accountingIntegrationContractClient.uploadDocumentMigration(
                        {
                            params: { workspaceId, entityId, source },
                            body: documentIds,
                            extraHeaders: {
                                [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                            },
                        },
                    );

                return response.body;
            } catch (error) {
                rethrowAsMigrationError(error);
            }
        },
        onSuccess: useInvalidateMigrationQueriesOnSuccess(entityId),
    });
}

export function useSubmitQbdJsonDocumentMigrationMutation(params: {
    workspaceId: string;
    entityId: number;
}) {
    const { workspaceId, entityId } = params;

    return useMutation({
        mutationFn: async (input: {
            documentId: number;
            confirmWarnings?: boolean;
        }) => {
            try {
                const response =
                    await accountingIntegrationContractClient.uploadQbdJsonDocumentMigration(
                        {
                            params: { workspaceId, entityId },
                            body: {
                                documentId: input.documentId,
                                confirmWarnings: input.confirmWarnings,
                            },
                            extraHeaders: {
                                [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                            },
                        },
                    );

                return response.body;
            } catch (error) {
                rethrowAsMigrationError(error);
            }
        },
        onSuccess: useInvalidateMigrationQueriesOnSuccess(entityId),
    });
}

interface DocumentMigrationValidation {
    issues: DocumentMigrationIssueDto[];
    coaCrossFileRejection?: ImportRejection;
}

function parseCoaCrossFileValidationError(
    error: unknown,
): DocumentMigrationValidation | null {
    if (!isAxiosError(error) || error.response?.status !== 400) {
        return null;
    }
    const parsed = documentMigrationUploadErrorSchema.safeParse(
        error.response.data,
    );
    if (
        !parsed.success ||
        parsed.data.code !== DocumentMigrationErrorCode.IMPORT_REJECTED ||
        parsed.data.file !== "coa" ||
        parsed.data.rejection == null
    ) {
        return null;
    }
    return {
        issues: [],
        coaCrossFileRejection: parsed.data.rejection,
    };
}

// An empty issues list is the gate for finalizing.
export function useValidateDocumentMigration(params: {
    workspaceId: string;
    entityId: number;
    source: CoADocumentMigrationSource;
    documentIds: SubmitDocumentMigrationParams | null;
    enabled: boolean;
}) {
    const { workspaceId, entityId, source, documentIds, enabled } = params;

    return useQuery({
        queryKey: [
            "document-migration-validation",
            workspaceId,
            entityId,
            source,
            documentIds,
        ],
        queryFn: async (): Promise<DocumentMigrationValidation> => {
            if (!documentIds) {
                return { issues: [] };
            }
            try {
                const response =
                    await accountingIntegrationContractClient.validateDocumentMigration(
                        {
                            params: { workspaceId, entityId, source },
                            body: documentIds,
                            extraHeaders: {
                                [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                            },
                        },
                    );
                return { issues: response.body.issues };
            } catch (error) {
                const coaCrossFileRejection =
                    parseCoaCrossFileValidationError(error);
                if (coaCrossFileRejection) {
                    return coaCrossFileRejection;
                }
                rethrowAsMigrationError(error);
                throw error;
            }
        },
        enabled: enabled && documentIds !== null && !!workspaceId && !!entityId,
        // documentIds changes rotate the query key, so never re-parse files on
        // window focus/reconnect.
        staleTime: Infinity,
    });
}

export function useUpdateMigrationStartDateMutation(params: {
    workspaceId: string;
    entityId: number;
}) {
    const { workspaceId, entityId } = params;
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (startDate: string | null) => {
            const response =
                await accountingIntegrationContractClient.updateMigrationStartDate(
                    {
                        params: { workspaceId, entityId },
                        body: { startDate },
                    },
                );
            return response.body.accountingMigration;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountingMigrationQueryKeys.byEntity(entityId),
            });
            queryClient.invalidateQueries({
                queryKey: accountingMigrationQueryKeys.byWorkspace(workspaceId),
            });
            invalidateAllTaskQueries(queryClient);
        },
    });
}

export function useUpdateMigrationEndDateMutation(params: {
    workspaceId: string;
    entityId: number;
}) {
    const { workspaceId, entityId } = params;
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (endDate: string) => {
            const response =
                await accountingIntegrationContractClient.updateMigrationEndDate(
                    {
                        params: { workspaceId, entityId },
                        body: { endDate },
                        extraHeaders: {
                            [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                        },
                    },
                );
            return response.body.accountingMigration;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountingMigrationQueryKeys.byEntity(entityId),
            });
            queryClient.invalidateQueries({
                queryKey: accountingMigrationQueryKeys.byWorkspace(workspaceId),
            });
            invalidateAllTaskQueries(queryClient);
            queryClient.invalidateQueries({
                queryKey: getEntitiesAccountsQueryKey(workspaceId),
            });
        },
    });
}

export function useSetQbdFileMigrationUploadSourceMutation(params: {
    workspaceId: string;
    entityId: number;
}) {
    const { workspaceId, entityId } = params;
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (source: QbdFileMigrationUploadSource) => {
            const response =
                await accountingIntegrationContractClient.setQbdFileMigrationUploadSource(
                    {
                        params: { workspaceId, entityId },
                        body: { source },
                    },
                );
            return response.body.source;
        },
        onSuccess: () => {
            invalidateAllTaskQueries(queryClient);
        },
    });
}
